import React, { useMemo, useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";

/**
 * Client-portal widget shown to APPROVED orgs so they can copy their
 * "Standard-Verified" embed code and drop it on their own website.
 *
 * Props:
 *   badgeSlug — the org's public HMAC slug from /api/alignment/status/mine
 *   orgName   — for display only
 */
export default function StandardBadgeWidget({ badgeSlug, orgName }) {
  const [copiedKey, setCopiedKey] = useState("");

  const publicUrl = "https://dealteam6.us";
  const badgeUrl = `${publicUrl}/api/badge/${badgeSlug}.svg`;
  const verifyUrl = `${publicUrl}/standard-verified/${badgeSlug}`;

  const snippets = useMemo(() => ({
    html: `<a href="${verifyUrl}" target="_blank" rel="noopener">\n  <img src="${badgeUrl}" alt="DT6 Standard-Verified — ${orgName || "Approved Org"}" height="48" />\n</a>`,
    html_light: `<a href="${verifyUrl}" target="_blank" rel="noopener">\n  <img src="${badgeUrl}?theme=light" alt="DT6 Standard-Verified — ${orgName || "Approved Org"}" height="48" />\n</a>`,
    markdown: `[![DT6 Standard-Verified](${badgeUrl})](${verifyUrl})`,
    url: badgeUrl,
  }), [badgeUrl, verifyUrl, orgName]);

  const copy = async (key) => {
    try {
      await navigator.clipboard.writeText(snippets[key]);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1600);
    } catch { /* clipboard may be blocked; user can select manually */ }
  };

  if (!badgeSlug) return null;

  return (
    <div className="dt6-card p-6 mb-6" data-testid="badge-widget">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <div className="font-mono text-[0.7rem] tracking-[0.14em] text-[color:var(--dt6-primary)] mb-1">
            / EMBED YOUR STANDARD-VERIFIED BADGE
          </div>
          <div className="text-sm text-[color:var(--dt6-muted)] leading-relaxed max-w-xl">
            Show your visitors that {orgName || "your organization"} passed the DT6 Standard.
            Drop the snippet on your site — every impression links back to the verification page.
          </div>
        </div>
        <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="dt6-btn text-xs" data-testid="badge-verify-link">
          Preview verify page <ExternalLink size={12} />
        </a>
      </div>

      {/* Live badge preview — dark + light */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        <div className="p-4 rounded" style={{ background: "#0B0E14", border: "1px solid var(--dt6-border)" }}>
          <img src={badgeUrl} alt="DT6 Standard-Verified badge, dark theme" style={{ height: 48 }} data-testid="badge-preview-dark" />
          <div className="font-mono text-[0.65rem] text-[color:var(--dt6-muted)] mt-2">/ DARK (default)</div>
        </div>
        <div className="p-4 rounded" style={{ background: "#EDE8DC", border: "1px solid var(--dt6-border)" }}>
          <img src={`${badgeUrl}?theme=light`} alt="DT6 Standard-Verified badge, light theme" style={{ height: 48 }} data-testid="badge-preview-light" />
          <div className="font-mono text-[0.65rem] mt-2" style={{ color: "#666" }}>/ LIGHT</div>
        </div>
      </div>

      {/* Copyable snippets */}
      {[
        { key: "html", label: "HTML (dark)", testId: "badge-copy-html" },
        { key: "html_light", label: "HTML (light theme)", testId: "badge-copy-html-light" },
        { key: "markdown", label: "Markdown", testId: "badge-copy-markdown" },
        { key: "url", label: "SVG URL", testId: "badge-copy-url" },
      ].map(({ key, label, testId }) => (
        <div key={key} className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono text-[0.65rem] uppercase tracking-widest text-[color:var(--dt6-muted)]">
              / {label}
            </span>
            <button
              type="button"
              onClick={() => copy(key)}
              className="dt6-btn text-xs"
              data-testid={testId}
            >
              {copiedKey === key ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
          <pre
            className="text-xs overflow-x-auto p-3 rounded"
            style={{ background: "var(--dt6-bg)", border: "1px solid var(--dt6-border)", color: "var(--dt6-text)" }}
            data-testid={`${testId}-content`}
          >
            {snippets[key]}
          </pre>
        </div>
      ))}
    </div>
  );
}
